import { useMutation, useQuery, useQueryClient } from 'react-query';
import axiosInstance from '../config/Axios';
import { useCustomToast } from '../pages/Contexts/ToastContext';

/**
 * Happy Hour — instant promo banner with countdown on top of whatever the
 * screens are playing. Backend: HappyHourController.
 *
 *   POST /happy-hour/start         { promo_text, promo_text_en?, style,
 *                                    promo_image_url?, duration_minutes,
 *                                    device_ids? (null/absent = all) }
 *   POST /happy-hour/{id}/end
 *   GET  /happy-hour/active        → { data: { session: {...} | null } }
 *   GET  /happy-hour/history       → { data: { sessions: [...] } }
 *   POST /happy-hour/upload-image  multipart { image } → { url }
 *
 * NOTE: active/history return a CompanyResource, which Laravel wraps in a
 * top-level `data` key (upload-image uses a plain json() and is NOT wrapped).
 * The hooks below unwrap that envelope so components read `.session` /
 * `.sessions` directly; `res?.data ?? res` stays safe if wrapping is ever
 * disabled.
 */

const ACTIVE_KEY    = 'happyHourActive';
const HISTORY_KEY   = 'happyHourHistory';
const TEMPLATES_KEY = 'happyHourTemplates';

export const useGetActiveHappyHour = () =>
  useQuery(
    ACTIVE_KEY,
    async () => {
      const res = (await axiosInstance.get('/happy-hour/active')).data;
      return res?.data ?? res;
    },
    {
      // Keeps the dashboard's countdown card honest after the scheduled
      // happy-hour:expire command flips the session to ENDED.
      refetchInterval: 15_000,
      staleTime: 10_000,
    }
  );

export const useGetHappyHourHistory = () =>
  useQuery(
    HISTORY_KEY,
    async () => {
      const res = (await axiosInstance.get('/happy-hour/history')).data;
      return res?.data ?? res;
    },
    { staleTime: 30_000 }
  );

export const useStartHappyHour = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((payload) => axiosInstance.post('/happy-hour/start', payload), {
    onSuccess: () => {
      qc.invalidateQueries(ACTIVE_KEY);
      qc.invalidateQueries(HISTORY_KEY);
      qc.invalidateQueries(TEMPLATES_KEY);
      qc.invalidateQueries('happyHourTemplateHistory');
      showToast('success', 'Happy Hour', 'Promo is live on your screens! 🔥');
    },
    onError: (err) => showToast('error', 'Happy Hour', err.response?.data?.message),
  });
};

export const useEndHappyHour = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((id) => axiosInstance.post(`/happy-hour/${id}/end`), {
    onSuccess: () => {
      qc.invalidateQueries(ACTIVE_KEY);
      qc.invalidateQueries(HISTORY_KEY);
      qc.invalidateQueries(TEMPLATES_KEY);
      showToast('success', 'Happy Hour', 'Promo ended.');
    },
    onError: (err) => showToast('error', 'Happy Hour', err.response?.data?.message),
  });
};

/* ──────────────────────────────────────────────────────────────────────
 * Templates — reusable promos with grouped usage history (primary view).
 *   GET    /happy-hour/templates              → { data: { templates: [...] } }
 *   GET    /happy-hour/templates/{id}/history → { data: { sessions: [...] } }
 *   PUT    /happy-hour/templates/{id}         edit text/style/image
 *   DELETE /happy-hour/templates/{id}         soft delete
 * All CompanyResource-wrapped, so unwrap `res?.data ?? res` like above.
 * ─────────────────────────────────────────────────────────────────── */

export const useGetHappyHourTemplates = () =>
  useQuery(
    TEMPLATES_KEY,
    async () => {
      const res = (await axiosInstance.get('/happy-hour/templates')).data;
      return res?.data ?? res;
    },
    { staleTime: 30_000 }
  );

/** Lazy per-template run history — only fetched once its card is expanded. */
export const useGetTemplateHistory = (templateId, enabled = true) =>
  useQuery(
    ['happyHourTemplateHistory', templateId],
    async () => {
      const res = (await axiosInstance.get(`/happy-hour/templates/${templateId}/history`)).data;
      return res?.data ?? res;
    },
    { enabled: !!templateId && enabled, staleTime: 30_000 }
  );

export const useUpdateHappyHourTemplate = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(
    ({ id, ...payload }) => axiosInstance.put(`/happy-hour/templates/${id}`, payload),
    {
      onSuccess: () => {
        qc.invalidateQueries(TEMPLATES_KEY);
        showToast('success', 'Happy Hour', 'Promo updated.');
      },
      onError: (err) => showToast('error', 'Happy Hour', err.response?.data?.message),
    }
  );
};

export const useDeleteHappyHourTemplate = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((id) => axiosInstance.delete(`/happy-hour/templates/${id}`), {
    onSuccess: () => {
      qc.invalidateQueries(TEMPLATES_KEY);
      showToast('success', 'Happy Hour', 'Promo deleted.');
    },
    onError: (err) => showToast('error', 'Happy Hour', err.response?.data?.message),
  });
};

/** Multipart product-photo upload; resolves to the public URL string. */
export const useUploadHappyHourImage = () => {
  const { showToast } = useCustomToast();
  return useMutation(
    async (file) => {
      const form = new FormData();
      form.append('image', file);
      const { data } = await axiosInstance.post('/happy-hour/upload-image', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data.url;
    },
    { onError: (err) => showToast('error', 'Happy Hour', err.response?.data?.message) }
  );
};

/** GET /folder/media filtered to IMAGE — backs the "From Media" product
 *  photo picker. Same shape as the birthday audio library query.
 *  `recursive: 1` lists files from every folder, not just root — this
 *  dialog has no folder-navigation UI. */
export const useGetImageLibrary = (enabled = true) =>
  useQuery(
    'happyHourImageLibrary',
    async () => (await axiosInstance.get('/folder/media', {
      params: { section: 'MEDIA', type: 'IMAGE', recursive: 1, per_page: 200, page: 1 },
    })).data,
    { enabled, staleTime: 30_000 }
  );
